<template>
  <DashboardPanel
    panel-id="v-pills-workexperience"
    aria-labelledby="v-pills-workexperience-tab"
    title="Work experience"
    subtitle="Employment history and career preferences"
  >
    <div class="hc-panel__section">
      <div class="hc-panel__section-head">
        <h3>Total experience</h3>
        <ProfileActionBtn variant="edit" id="btnedit" @click.prevent="toggleExpEdit">Edit</ProfileActionBtn>
      </div>
      <div class="hc-panel__section-body hc-profile-details">
      <form>
        <div id="a" v-if="!edit_exp">
          <ProfileDetailGrid single>
            <ProfileDetailItem label="Total experience" :value="user_exp" required />
          </ProfileDetailGrid>
        </div>
        <div id="b" class="hc-profile-edit-form" v-if="edit_exp">
          <ProfileDetailGrid single>
            <ProfileEditItem
              label="Total experience"
              required
              :error="(typeof validate.response.exp != 'undefined' && !validate.response.exp.val) ? validate.response.exp.msg : ''"
            >
              <v-select
                id="total_experience"
                name="totalExp"
                v-model="toEdit.exp"
                :items="exp.options"
                item-value="value"
                item-text="text"
                placeholder="Select experience"
                @input="validate.checkIfEmpty(toEdit.exp, 'exp', 'Select total experience')"
                clearable
                outlined
                dense
                hide-details
              />
            </ProfileEditItem>
          </ProfileDetailGrid>
          <p v-if="exp_error" class="hc-profile-edit-form__error">{{ exp_err_msg }}</p>
          <ProfileEditFooter @cancel="cancelExpEdit" @save="toEditExp" />
        </div>
      </form>
      </div>
    </div>
    <div class="hc-panel__section">
      <div class="hc-panel__section-head">
        <h3>Employment history</h3>
        <ProfileActionBtn variant="add" id="btnchng" @click.prevent="toggleAddCompany">Add</ProfileActionBtn>
      </div>
      <div class="hc-panel__section-body hc-profile-details">
      <form>
        <div id="d" class="hc-profile-edit-form" v-if="add_company">
          <ProfileDetailGrid>
            <ProfileEditItem
              label="Company name"
              required
              wide
              :error="(typeof validate.response.company_name != 'undefined' && !validate.response.company_name.val) ? validate.response.company_name.msg : ''"
            >
              <v-text-field
                v-model="toEdit.company.worked_company_name"
                type="text"
                placeholder="Company name"
                @input="validate.checkIfEmpty(toEdit.company.worked_company_name, 'company_name', 'Enter company name')"
                outlined
                dense
                hide-details
              />
            </ProfileEditItem>
            <ProfileEditItem
              label="Designation"
              required
              wide
              :error="(typeof validate.response.designation != 'undefined' && !validate.response.designation.val) ? validate.response.designation.msg : ''"
            >
              <v-autocomplete
                id="designation"
                name="designation"
                v-model="toEdit.company.designation"
                :items="jobrole"
                item-value="text"
                item-text="text"
                placeholder="Designation"
                @input="validate.checkIfEmpty(toEdit.company.designation, 'designation', 'Select designation')"
                clearable
                outlined
                dense
                hide-details
              />
            </ProfileEditItem>
            <ProfileEditItem
              label="Start date"
              required
              :error="(typeof validate.response.duration_from != 'undefined' && !validate.response.duration_from.val) ? validate.response.duration_from.msg : ''"
            >
              <input
                type="date"
                v-model="toEdit.company.work_duration_from"
                class="hc-field-input"
                @input="validate.checkIfEmpty(toEdit.company.work_duration_from, 'duration_from', 'Enter working from')"
              >
            </ProfileEditItem>
            <ProfileEditItem
              label="End date"
              :required="showEndDatePicker"
              :error="(typeof validate.response.duration_to != 'undefined' && !validate.response.duration_to.val) ? validate.response.duration_to.msg : ''"
            >
              <input
                v-if="showEndDatePicker"
                type="date"
                v-model="toEdit.company.work_duration_to"
                class="hc-field-input"
                @input="validate.checkIfEmpty(toEdit.company.work_duration_to, 'duration_to', 'Enter working to')"
              >
              <input
                v-else
                type="text"
                class="hc-field-input hc-field-input--readonly"
                value="Present"
                readonly
                tabindex="-1"
              >
            </ProfileEditItem>
            <ProfileEditItem v-if="!current_company_status" label="Currently working" wide>
              <label class="hc-work-toggle">
                <input
                  type="checkbox"
                  class="hc-work-toggle__input"
                  v-model="currentlyWorking"
                >
                <span class="hc-work-toggle__control" aria-hidden="true"></span>
                <span class="hc-work-toggle__text">I currently work here</span>
              </label>
            </ProfileEditItem>
            <ProfileEditItem
              label="Skills"
              required
              wide
              :error="(typeof validate.response.skills != 'undefined' && !validate.response.skills.val) ? validate.response.skills.msg : ''"
            >
              <v-autocomplete
                id="skills"
                name="skills"
                v-model="toEdit.company.skills"
                :items="skills"
                item-value="text"
                item-text="text"
                placeholder="Skills"
                @input="validate.checkIfEmpty(toEdit.company.skills, 'skills', 'Select skills')"
                clearable
                outlined
                dense
                hide-details
                chips
                multiple
                deletable-chips
              />
            </ProfileEditItem>
            <ProfileEditItem label="Salary / month">
              <v-text-field
                type="number"
                name="salary"
                v-model="toEdit.company.current_salary"
                placeholder="Monthly salary"
                outlined
                dense
                hide-details
              />
            </ProfileEditItem>
          </ProfileDetailGrid>
          <p v-if="company_error" class="hc-profile-edit-form__error">{{ company_err_msg }}</p>
          <ProfileEditFooter @cancel="cancelAddCompany" @save="addCompany" />
        </div>
        <div v-if="current_company || prev_companies.length" class="hc-record-list">
          <ProfileRecordCard
            v-if="current_company"
            :title="current_company.worked_company_name"
            :subtitle="current_company.designation"
            badge="Current"
          >
            <template #actions>
              <ProfileActionBtn variant="delete" @click.prevent="removeCompany(current_company.id)">Delete</ProfileActionBtn>
            </template>
            <ProfileDetailGrid>
              <ProfileDetailItem
                label="Duration"
                :value="current_company.work_duration_month_from && current_company.work_duration_year_from ? `${getMonth(current_company.work_duration_month_from)} ${current_company.work_duration_year_from} – Present` : ''"
                required
                wide
              />
              <ProfileDetailItem label="Skills" :value="current_company.skills" required wide />
              <ProfileDetailItem label="Salary / month" :value="current_company.current_salary" />
            </ProfileDetailGrid>
          </ProfileRecordCard>

          <ProfileRecordCard
            v-for="(company, index) in prev_companies"
            :key="company.id || index"
            :title="company.worked_company_name"
            :subtitle="company.designation"
          >
            <template #actions>
              <ProfileActionBtn variant="delete" @click.prevent="removeCompany(company.id)">Delete</ProfileActionBtn>
            </template>
            <ProfileDetailGrid>
              <ProfileDetailItem
                label="Duration"
                :value="company.work_duration_month_from && company.work_duration_year_from ? `${getMonth(company.work_duration_month_from)} ${company.work_duration_year_from} – ${getMonth(company.work_duration_month_to)} ${company.work_duration_year_to}` : ''"
                required
                wide
              />
              <ProfileDetailItem label="Skills" :value="company.skills" required wide />
              <ProfileDetailItem label="Salary / month" :value="company.current_salary" />
            </ProfileDetailGrid>
          </ProfileRecordCard>
        </div>
        <div class="hc-panel__empty hc-panel__empty--compact" v-else>
          <p class="mb-0">No employment history added yet.</p>
        </div>
      </form>
      </div>
    </div>
    <div class="hc-panel__section">
      <div class="hc-panel__section-head">
        <h3>Career preference</h3>
        <ProfileActionBtn variant="edit" id="gh" @click.prevent="toggleCareerEdit">Edit</ProfileActionBtn>
      </div>
      <div class="hc-panel__section-body hc-profile-details">
      <form>
        <div id="g" v-if="!edit_career_pref">
          <ProfileDetailGrid>
            <ProfileDetailItem label="Job role" :value="user_role.join(', ')" required wide />
            <ProfileDetailItem label="Preferred location" :value="user_details.user_city && user_details.user_city.city_name" required />
            <ProfileDetailItem label="Expected salary (annual)" :value="user_career.preferred_salary" />
          </ProfileDetailGrid>
        </div>
        <div id="h" class="hc-profile-edit-form" v-if="edit_career_pref">
          <ProfileDetailGrid>
            <ProfileEditItem
              label="Job role"
              required
              wide
              :error="(typeof validate.response.jobrole != 'undefined' && !validate.response.jobrole.val) ? validate.response.jobrole.msg : ''"
            >
              <v-autocomplete
                v-model="toEdit.career_preference.jobrole"
                :items="jobrole"
                item-value="value"
                item-text="text"
                placeholder="Job role"
                @input="validate.jobroleValidation(toEdit.career_preference.jobrole, 'jobrole')"
                clearable
                chips
                outlined
                dense
                hide-details
                multiple
                deletable-chips
                hide-selected
              />
            </ProfileEditItem>
            <ProfileEditItem
              label="Preferred location"
              required
              :error="(typeof validate.response.cities != 'undefined' && !validate.response.cities.val) ? validate.response.cities.msg : ''"
            >
              <v-autocomplete
                v-model="toEdit.career_preference.preferred_location"
                :items="cities"
                item-text="text"
                item-value="id"
                placeholder="Preferred location"
                @input="validate.checkIfEmpty(toEdit.career_preference.preferred_location,'cities','Select preferred location')"
                clearable
                outlined
                dense
                hide-details
              >
                <template slot="item" slot-scope="data">
                  <template v-if="typeof(data.item) != 'object'">
                    <v-list-item-content v-text="data.item.text"/>
                  </template>
                  <template v-else>
                    <v-list-item-content>
                      <v-list-item-title v-html="data.item.text"/>
                      <v-list-item-sub-title v-html="data.item.group"/>
                    </v-list-item-content>
                  </template>
                </template>
              </v-autocomplete>
            </ProfileEditItem>
            <ProfileEditItem label="Expected salary (annual)">
              <input
                type="number"
                v-model="toEdit.career_preference.salary"
                class="hc-field-input"
                name="expsal"
                min="0"
                placeholder="Annual salary"
              >
            </ProfileEditItem>
          </ProfileDetailGrid>
          <p v-if="career_error" class="hc-profile-edit-form__error">{{ career_err_msg }}</p>
          <ProfileEditFooter @cancel="cancelCareerEdit" @save="updateCareer" />
        </div>
      </form>
      </div>
    </div>
    <ProfileToast ref="profileToast" />
  </DashboardPanel>
</template>

<script>
import Actions from '../libraries/apiActions.js';
import validation from '../libraries/validFormat' ;
import DashboardPanel from './Dashboard/DashboardPanel.vue';
import ProfileDetailGrid from './Dashboard/ProfileDetailGrid.vue';
import ProfileDetailItem from './Dashboard/ProfileDetailItem.vue';
import ProfileRecordCard from './Dashboard/ProfileRecordCard.vue';
import ProfileToast from './Dashboard/ProfileToast.vue';
import ProfileActionBtn from './Dashboard/ProfileActionBtn.vue';
import ProfileEditFooter from './Dashboard/ProfileEditFooter.vue';
import ProfileEditItem from './Dashboard/ProfileEditItem.vue';
// import 'bootstrap-vue/dist/bootstrap-vue.css'
import '../assets/hire.css';
export default {
    name:'ExperienceDetails',
    components: { DashboardPanel, ProfileDetailGrid, ProfileDetailItem, ProfileRecordCard, ProfileToast, ProfileActionBtn, ProfileEditFooter, ProfileEditItem },
    data(){
      return {
        validate: new validation(),
        action: new Actions(),
        user_details:this.$attrs.user.personal_details,
        user_career : this.$attrs.user.careerPreferencesDetail,
        current_company : this.$attrs.user.current_company,
        current_company_status : this.$attrs.user.current_company?1:0,
        prev_companies : this.$attrs.user.previous_companies,
        user_exp: null,
        edit_exp: false,
        add_company:false,
        edit_prev_company: false,
        edit_career_pref:false,
        designations:[],
        skills:[],
        user_role:[],
        cities:this.$store.state.states.locations.locations,
        jobrole:this.$store.state.jobrole.jobrole.jobrole,
        can_edit:0,
        toEdit:{
          exp:null,
          company:{
            worked_company_name:null,
            designation: null,
            work_duration_from:null,
            work_duration_to:null,
            skills:null,
            current_salary:0,
            employer_status:0,
            role:1
          },
          career_preference:{
            jobrole:[],
            preferred_location:null,
            salary:null ,
            job_type:2
          }
        },
        exp: {
          options: [
            { value: 1, text: "0 Month(Fresher)" },
            { value: 6, text: "06 Months" },
            { value: 12, text: "1 Year" },
            { value: 24, text: "2 year" },
            { value: 36, text: "3 year" },
            { value: 48, text: "4 year" },
            { value: 60, text: "5 year" },
            { value: 72, text: "6 year" },
            { value: 84, text: "6+ year" },
          ],
        },
        exp_error : false,
        exp_err_msg:null,
        company_error:false,
        company_err_msg:null,
        career_error:false,
        career_err_msg:null,
      }
    },
    computed: {
      currentlyWorking: {
        get() {
          return this.toEdit.company.employer_status === 1;
        },
        set(val) {
          this.toEdit.company.employer_status = val ? 1 : 0;
          if (val) {
            this.toEdit.company.work_duration_to = null;
          }
        },
      },
      showEndDatePicker() {
        return !this.currentlyWorking || this.current_company_status;
      },
    },
    beforeMount(){
      if(this.user_details.total_work_experience == 0){
        this.user_details.total_work_experience = 1;
      }
      this.getExp(this.user_details.total_work_experience);
      this.getDesignations();
      this.getSkills();
      this.setJobRole();
      this.setCareerDetails();
      this.setExperienceDetails();
    },
    methods: {
      getExp(id){
        this.toEdit.exp = id;
        this.exp.options.forEach(e => {
          if(e.value == id){
            this.user_exp = e.text;
          }
        })
      },
      getMonth(data){
        var mm = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
        return mm[data+1]
      },
      setCareerDetails(){
        if(this.user_career.prefered_city == null){
          this.toEdit.career_preference.preferred_location = this.user_details?.user_city.city_id;
        }else{
          this.toEdit.career_preference.preferred_location = this.user_career.prefered_city; 
        }
        this.toEdit.career_preference.salary = (this.user_career.preferred_salary)?this.user_career.preferred_salary:null;
      },
      setExperienceDetails(){},
      getDesignations(){
        this.action.getDesignations().then(res => {
          res.designations.forEach(desig => {
            this.designations.push({value:desig.designation_id, text:desig.designation_name})
          })
        })
      },
      getSkills(){
        this.action.getSkills().then(res => {
          res.skills.forEach(skill => {
            this.skills.push({value:skill.id, text:skill.skill});
          })
        })
      },
      setJobRole: function(){
        this.user_role = [];
        this.toEdit.career_preference.jobrole =[];
        this.$store.state.jobrole.jobrole.jobrole.forEach((element) => {
            if(element.value == this.user_career.fst_prefered_role){
              this.user_role.push(element.text)
            }
            if(element.value == this.user_career.snd_prefered_role){
              this.user_role.push(element.text)
            }
            if(element.value == this.user_career.trd_prefered_role){
              this.user_role.push(element.text)
            }
        })
          if(this.user_career.fst_prefered_role){
            this.toEdit.career_preference.jobrole.push(this.user_career.fst_prefered_role);
          }
          if(this.user_career.snd_prefered_role){
            this.toEdit.career_preference.jobrole.push(this.user_career.snd_prefered_role);
          }
          if(this.user_career.trd_prefered_role){
            this.toEdit.career_preference.jobrole.push(this.user_career.trd_prefered_role);
          }
      },
      toEditExp(){
        this.can_edit = 0;
        this.exp_error = false;
        this.exp_err_msg = null;

        if(this.validate.checkIfEmpty(this.toEdit.exp, "exp", 'Select total experience')?.val === false){
          this.can_edit++;
        }
        if(this.can_edit == 0){
          this.action.editExperience(this.toEdit.exp,this.$cookies.get("hire-seeker").token).then(res => {
            if(res.message == 'Work experience Updated...'){
              this.edit_exp = false;
              this.getExp(this.toEdit.exp)
              this.$refs.profileToast.show('Total experience saved');
            }else{
              this.exp_error = true;
              this.exp_err_msg = "Network error! Please check your internet connection.";
            }
          }).catch(err =>{
            console.log(err);
            this.exp_error = true;
            if(window.navigator.onLine){
              this.exp_err_msg = "System error! Please try again.";
            }else{
              this.exp_err_msg = "Network error! Please check your internet connection.";
            }
          })
        }else{
          this.exp_error = true;
          this.exp_err_msg = "Fill all mandatory fields.";
        }
      },
      addCompany(){
        this.can_edit = 0;
        this.company_error = false;
        this.company_err_msg = null;
        
        if(this.validate.checkIfEmpty(this.toEdit.company.worked_company_name, "company_name", 'Enter company name')?.val === false){
          this.can_edit++;
        }
        if(this.validate.checkIfEmpty(this.toEdit.company.designation, "designation", 'Select designation')?.val === false){
          this.can_edit++;
        }
        if(this.validate.checkIfEmpty(this.toEdit.company.work_duration_from, "duration_from", 'Enter working from')?.val === false){
          this.can_edit++;
        }
        if(this.toEdit.company.employer_status == 0){
          if(this.validate.checkIfEmpty(this.toEdit.company.work_duration_to, "duration_to", 'Enter working to')?.val === false){
            this.can_edit++;
          }
        }
        if(this.validate.checkIfEmpty(this.toEdit.company.skills, "skills", 'Select skills')?.val === false){
          this.can_edit++;
        }
        if(this.can_edit == 0){
          this.toEdit.company.skills = (this.toEdit.company.skills)?this.toEdit.company.skills.join():null;
          this.toEdit.company.work_duration_from = this.validate.convertDate(this.toEdit.company.work_duration_from.split('-'));
          if (this.toEdit.company.employer_status == 0) {
            this.toEdit.company.work_duration_to = this.validate.convertDate(this.toEdit.company.work_duration_to.split('-'));
          } else {
            this.toEdit.company.work_duration_to = this.validate.convertDate((new Date()).toLocaleDateString('ko-KR').split('.'));
          }
          this.action.addExperience(this.toEdit.company,this.$cookies.get("hire-seeker").token).then(res => {
            if(res.message == 'Company details saved successfully'){
              this.add_company = false;
              this.$refs.profileToast.show('Employment details saved');
              if(this.toEdit.company.employer_status == 1){
                this.current_company = {
                  id:res.company.id,
                  worked_company_name: res.company.worked_company_name,
                  designation: res.company.designation,
                  work_duration_year_from: parseInt(res.company.work_duration_year_from),
                  work_duration_year_to: parseInt(res.company.work_duration_year_to),
                  work_duration_month_from: parseInt(res.company.work_duration_month_from),
                  work_duration_month_to: parseInt(res.company.work_duration_month_to),
                  skills: res.company.skills,
                  current_salary: res.company.current_salary 
                }
                this.current_company_status = 1;
              }else{
                this.prev_companies.push({
                  id:res.company.id,
                  worked_company_name: res.company.worked_company_name,
                  designation: res.company.designation,
                  work_duration_year_from: parseInt(res.company.work_duration_year_from),
                  work_duration_year_to: parseInt(res.company.work_duration_year_to),
                  work_duration_month_from: parseInt(res.company.work_duration_month_from),
                  work_duration_month_to: parseInt(res.company.work_duration_month_to),
                  skills: res.company.skills,
                  current_salary: res.company.current_salary
                });
              }
              this.resetFields();
            }else{
              this.company_error = true;
              this.company_err_msg = "Network error! Please check your internet connection.";
            }
          }).catch(err =>{
            console.log(err);
            this.company_error = true;
            if(window.navigator.onLine){
              this.company_err_msg = "System error! Please try again.";
            }else{
              this.company_err_msg = "Network error! Please check your internet connection.";
            }
          })
        }else{
          this.company_error = true;
          this.company_err_msg = "Fill all mandatory fields.";
        }
      },
      updateCareer(){
        this.can_edit = 0;
        this.career_error= false;
        this.career_err_msg = null;

        if (this.validate.jobroleValidation(this.toEdit.career_preference.jobrole, "jobrole")?.val === false) {
          this.can_edit++;
        }
        if(this.validate.checkIfEmpty(this.toEdit.career_preference.preferred_location, "cities", 'Select preferred location')?.val === false){
          this.can_edit++;
        }
        if(this.can_edit == 0){
          this.action.updateCareerPreference(this.toEdit.career_preference,this.$cookies.get("hire-seeker").token).then(res => {
            if(res.message =='Career Preferences Detail Updated...'){
              this.edit_career_pref = false;
              this.user_role = [];
              this.user_career.fst_prefered_role = (this.toEdit.career_preference.jobrole.length >= 1) ? this.toEdit.career_preference.jobrole[0] : null;
              this.user_career.snd_prefered_role = (this.toEdit.career_preference.jobrole.length >= 2) ? this.toEdit.career_preference.jobrole[1] : null;
              this.user_career.trd_prefered_role = (this.toEdit.career_preference.jobrole.length == 3) ? this.toEdit.career_preference.jobrole[2] : null;
              this.setJobRole();
              this.user_details.user_city.city_id = this.toEdit.career_preference.preferred_location;
              this.getCityNameFromID(this.toEdit.career_preference.preferred_location);
              this.user_career.preferred_salary = (this.toEdit.career_preference.salary)?this.toEdit.career_preference.salary:null;
              this.$refs.profileToast.show('Career preferences saved');
            }else{
              this.career_error = true;
              this.career_err_msg = "Network error! Please check your internet connection.";
            }
          }).catch(err =>{
            console.log(err);
            this.career_error = true;
            if(window.navigator.onLine){
              this.career_err_msg = "System error! Please try again.";
            }else{
              this.career_err_msg = "Network error! Please check your internet connection.";
            }
          })
        }else{
          this.career_error = true;
          this.career_err_msg = "Fill all mandatory fields.";
        }
      },
      getCityNameFromID: async function(id){
        await this.action.getStates().then(async (data) => {
          await data.states.forEach(async (element) => {
            element.cities.forEach(city => {
              if(city.city_id == id){
                this.user_details.user_city.city_name = city.city_name;
              }
            });
          });
        });
      },
      resetFields: function(){
        this.toEdit.exp = null;
        this.toEdit.company.worked_company_name = null;
        this.toEdit.company.designation = null;
        this.toEdit.company.work_duration_from = null;
        this.toEdit.company.work_duration_to = null;
        this.toEdit.company.skills = null;
        this.toEdit.company.current_salary = 0;
        this.toEdit.company.employees = 0;
        this.toEdit.company.role = [];
        this.toEdit.career_preference.jobrole = [];
        this.toEdit.career_preference.preferred_location = null;
        this.toEdit.career_preference.expected_salary = null;
        this.toEdit.career_preference.job_type = 2;
        this.toEdit.company.employer_status = 0;

        if(this.user_details.total_work_experience == 0){
          this.user_details.total_work_experience = 1;
        }
        this.getExp(this.user_details.total_work_experience);
        this.setJobRole();
        this.setCareerDetails();
        this.setExperienceDetails();
      },
      cancelExpEdit() {
        this.getExp(this.user_details.total_work_experience);
        this.exp_error = false;
        this.exp_err_msg = null;
        this.edit_exp = false;
      },
      toggleExpEdit() {
        if (this.edit_exp) {
          this.cancelExpEdit();
        } else {
          this.edit_exp = true;
        }
      },
      cancelAddCompany() {
        this.toEdit.company.worked_company_name = null;
        this.toEdit.company.designation = null;
        this.toEdit.company.work_duration_from = null;
        this.toEdit.company.work_duration_to = null;
        this.toEdit.company.skills = null;
        this.toEdit.company.current_salary = 0;
        this.toEdit.company.employer_status = 0;
        this.toEdit.company.role = 1;
        this.company_error = false;
        this.company_err_msg = null;
        this.add_company = false;
      },
      toggleAddCompany() {
        if (this.add_company) {
          this.cancelAddCompany();
        } else {
          this.add_company = true;
        }
      },
      cancelCareerEdit() {
        this.toEdit.career_preference.jobrole = [];
        this.setJobRole();
        this.setCareerDetails();
        this.career_error = false;
        this.career_err_msg = null;
        this.edit_career_pref = false;
      },
      toggleCareerEdit() {
        if (this.edit_career_pref) {
          this.cancelCareerEdit();
        } else {
          this.edit_career_pref = true;
        }
      },
      removeCompany(id){
        this.action.removeCompany(id,this.$cookies.get("hire-seeker").token).then(res => {
          if(res.message == 'Company deleted successfully'){
            console.log('deleted');
            this.prev_companies = this.prev_companies.filter(company => company.id !== id);
          }
        });
      }
    }
}
</script>

<style>
.hc-profile-edit-form .v-select.v-select--chips:not(.v-text-field--single-line).v-text-field--box .v-select__selections,
.hc-profile-edit-form .v-select.v-select--chips:not(.v-text-field--single-line).v-text-field--enclosed .v-select__selections {
  min-height: 0 !important;
}
.hc-profile-edit-form .v-text-field__details {
  display: none !important;
}
.hc-profile-edit-form .v-input__slot {
  margin-bottom: 0 !important;
}
</style>
<style scoped>
.error-feedback {
  position: static;
  width: 100%;
  margin-top: 0.35rem;
}
</style>